import { cn } from "../../lib/utils";
import type { Alert, CallRisk, RiskLevel, Severity } from "./types";

/**
 * Severity colours.
 *
 * Red/amber/green are reserved for risk here and used for nothing decorative, so that a
 * colour on this screen always means the same thing. Acknowledged alerts deliberately drop
 * to neutral: the alert has not gone away, but it has stopped being someone's problem to
 * notice.
 */
export function severityStyle(severity?: Severity | null): string {
  switch (severity) {
    case "critical":
      return "border-danger/40 bg-danger-soft text-danger";
    case "warn":
      return "border-warning/40 bg-warning-soft text-warning";
    case "info":
      return "border-line bg-soft text-ink-2";
    default:
      return "border-line bg-soft text-ink-3";
  }
}

/** Human-readable rule names. Falls back to the raw type so a new rule is never invisible. */
const ALERT_LABELS: Record<string, string> = {
  human_requested: "Asked for a human",
  negative_sentiment: "Caller upset",
  language_mismatch: "Language mismatch",
  agent_repetition: "AI repeating itself",
  user_questions: "Many questions",
  long_call: "Long call",
  stalled: "Stalled",
  repeat_human_request: "Human asked 3×",
  handoff_confirmed: "Human asked (confirmed)",
  loop_detected: "AI stuck in a loop",
  dead_air: "Dead air",
  pii_before_verification: "PII before verification",
  sensitive_disclosure: "PII disclosed (confirmed)",
  credential_shared: "Caller shared an OTP, PIN or card number",
};

export function alertLabel(alert: Alert): string {
  return ALERT_LABELS[alert.type] ?? alert.type.replace(/_/g, " ");
}

/** mm:ss since the alert first fired. The number a supervisor is actually judged on. */
export function formatAge(seconds?: number): string {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function AlertBadge({ alert, className }: { alert: Alert; className?: string }) {

  const acknowledged = alert.status === "acknowledged";

  return (
    <span
      title={alert.quoted_text ? `${alert.detail}\n\n“${alert.quoted_text}”` : alert.detail}
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium",
        acknowledged ? "border-line bg-soft text-ink-3" : severityStyle(alert.severity),
        className,
      )}
    >
      {alert.overdue ? <span aria-hidden>!</span> : null}
      {alertLabel(alert)}
      <span className="font-mono tabular-nums opacity-70">{formatAge(alert.age_seconds)}</span>
      {acknowledged ? <span className="opacity-70">seen</span> : null}
    </span>
  );
}

/**
 * P0 risk score chip. Hidden at zero so a quiet wall stays quiet; amber at 30,
 * red at 60 or any red rule, matching core's scoring.
 *
 * Non-finite scores render nothing: the payload is cast JSON with no runtime
 * validation, and "NaN · Watch" on a supervisor's wall helps nobody. An unknown
 * level falls back to the score thresholds so a mismatched payload still reads
 * at the urgency its number earns.
 */
export function RiskBadge({ risk, className }: { risk?: CallRisk | null; className?: string }) {
  if (!risk || typeof risk.score !== "number" || !Number.isFinite(risk.score) || risk.score <= 0) return null;
  const level: RiskLevel =
    risk.level === "red" || risk.level === "amber" || risk.level === "green"
      ? risk.level
      : risk.score >= 60
        ? "red"
        : risk.score >= 30
          ? "amber"
          : "green";
  return (
    <span
      title={`Risk score ${risk.score}`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums",
        level === "red"
          ? "border-danger/40 bg-danger-soft text-danger"
          : level === "amber"
            ? "border-warning/40 bg-warning-soft text-warning"
            : "border-line bg-soft text-ink-3",
        className,
      )}
    >
      {Math.round(risk.score)}
    </span>
  );
}
